import { type ReactNode, useId, useState } from 'react';

import { Collapse, Typography } from '@material-ui/core';

import classNames from 'classnames';

import { Button, IconChevronRight } from '@spotify-internal/encore-web';
import { ConversationsV2ToolConfirmationResponseDecision } from '@spotify/confidence-graphql';

import type { ToolConfirmationRenderProps } from '../../extensions';
import { ResourceDisplayNameProvider } from '../ToolConfirmationMessage/ResourceDisplayNameContext';
import {
  isPlainObject,
  resolveInputBody,
} from '../ToolConfirmationMessage/inputUtils';

import styles from './ResourceConfirmationCard.module.css';

export const CONFIRMATION_ACTIONS = [
  {
    decision: ConversationsV2ToolConfirmationResponseDecision.Allow,
    label: 'Approve',
    variant: 'primary' as const,
  },
  {
    decision: ConversationsV2ToolConfirmationResponseDecision.AllowAll,
    label: 'Approve all',
    variant: 'tertiary' as const,
  },
  {
    decision: ConversationsV2ToolConfirmationResponseDecision.Deny,
    label: 'Cancel',
    variant: 'tertiary' as const,
  },
];

interface ResourceConfirmationCardProps extends ToolConfirmationRenderProps {
  icon?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  resourceHints?: string[];
}

export const ResourceConfirmationCard = ({
  icon,
  title,
  children,
  input,
  resourceHints,
  actionsDisabled,
  pendingDecision,
  error,
  onDecide,
}: ResourceConfirmationCardProps) => {
  const [rawExpanded, setRawExpanded] = useState(false);
  const rawContentId = useId();
  const parsedInput = isPlainObject(input) ? resolveInputBody(input) : input;

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        {icon && (
          <span className={styles.icon} aria-hidden="true">
            {icon}
          </span>
        )}
        <Typography variant="body2" className={styles.title}>
          {title}
        </Typography>
      </div>
      <div className={styles.body}>
        {children && (
          <div className={styles.resourceContainer}>
            <ResourceDisplayNameProvider
              input={parsedInput}
              additionalNames={resourceHints}
            >
              {children}
            </ResourceDisplayNameProvider>
          </div>
        )}
        <div>
          <button
            type="button"
            className={styles.rawPayloadToggle}
            aria-expanded={rawExpanded}
            aria-controls={rawContentId}
            onClick={() => setRawExpanded(v => !v)}
          >
            View raw payload
            <IconChevronRight
              size="xsmall"
              className={classNames(styles.chevron, {
                [styles.chevronExpanded]: rawExpanded,
              })}
            />
          </button>
          <Collapse in={rawExpanded} unmountOnExit>
            <pre id={rawContentId} className={styles.rawPayloadContent}>
              {JSON.stringify(input, null, 2)}
            </pre>
          </Collapse>
        </div>
      </div>
      <hr className={styles.divider} />
      <div className={styles.actions}>
        <div className={styles.buttons}>
          {CONFIRMATION_ACTIONS.map(({ decision, label, variant }) => (
            <Button
              key={label}
              variant={variant}
              disabled={actionsDisabled || pendingDecision === decision}
              isLoading={pendingDecision === decision}
              loadingAriaLabel={`${label} in progress`}
              aria-busy={pendingDecision === decision}
              onClick={() => onDecide(decision)}
            >
              {label}
            </Button>
          ))}
        </div>
        {error && (
          <Typography variant="body2" color="error">
            {error.message}
          </Typography>
        )}
      </div>
    </div>
  );
};
